/**
 * Phase 15.1 — IndexedDB-backed KV storage for zustand persist.
 *
 * Why IndexedDB (а не localStorage):
 *   - localStorage имеет лимит ~5MB и блокирует main thread.
 *   - Нам нужно держать список каналов, курсоры sync и LRU последних
 *     сообщений каждого чата — это легко перерастает 5MB.
 *   - IDB работает асинхронно и имеет лимит ~50% свободного диска.
 *
 * Почему не idb-keyval: мы уже имеем `outbox.ts` собственной IDB-обёрткой
 * (~50 строк). Лишняя зависимость ради API из 3 методов — не нужна.
 *
 * Store-схема:
 *   db: 'orenburg56-cache' v1
 *   stores:
 *     - 'kv': { keyPath: 'k' }   // k: string, v: unknown
 */

import type { StateStorage } from 'zustand/middleware';

const DB_NAME = 'orenburg56-cache';
const DB_VERSION = 1;
const STORE = 'kv';

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'k' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function tx(mode: IDBTransactionMode): Promise<IDBObjectStore> {
  return openDb().then((db) => db.transaction(STORE, mode).objectStore(STORE));
}

export const idbStorage: StateStorage = {
  async getItem(name: string): Promise<string | null> {
    try {
      const store = await tx('readonly');
      return await new Promise((resolve, reject) => {
        const r = store.get(name);
        r.onsuccess = () => resolve((r.result as { k: string; v: string } | undefined)?.v ?? null);
        r.onerror = () => reject(r.error);
      });
    } catch {
      return null;
    }
  },
  async setItem(name: string, value: string): Promise<void> {
    try {
      const store = await tx('readwrite');
      await new Promise<void>((resolve, reject) => {
        const r = store.put({ k: name, v: value });
        r.onsuccess = () => resolve();
        r.onerror = () => reject(r.error);
      });
    } catch {
      // Silently ignore — персист это best-effort, UI не должен падать.
    }
  },
  async removeItem(name: string): Promise<void> {
    try {
      const store = await tx('readwrite');
      await new Promise<void>((resolve, reject) => {
        const r = store.delete(name);
        r.onsuccess = () => resolve();
        r.onerror = () => reject(r.error);
      });
    } catch {
      // ignore
    }
  },
};

/**
 * Полная очистка кэша — вызывать при logout, чтобы следующий юзер
 * не увидел чужие каналы/сообщения.
 */
export async function clearIdbCache(): Promise<void> {
  try {
    const store = await tx('readwrite');
    await new Promise<void>((resolve, reject) => {
      const r = store.clear();
      r.onsuccess = () => resolve();
      r.onerror = () => reject(r.error);
    });
  } catch {
    // ignore
  }
}
